import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import DashboardShell from './components/DashboardShell';
import TaxRefundAffidavitPanel from './components/TaxRefundAffidavitPanel';
import TrusteeRequirementDocumentLinks, {
  TrusteeDocumentMergePanel,
} from './components/TrusteeRequirementDocumentLinks';
import DSOFormPanel from './components/DSOFormPanel';
import ServiceDocumentsPanel from './components/ServiceDocumentsPanel';
import BNCCompliancePanel from './components/BNCCompliancePanel';
import FinancialManagementFilingPanel from './components/FinancialManagementFilingPanel';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.detail || `Request failed (${response.status})`);
  return data;
}

function formatDate(value) {
  if (!value) return 'No deadline';
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function isBeforeToday(value) {
  if (!value) return false;
  const due = new Date(value);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function priorityClasses(priority) {
  if (priority === 1) return 'border-red-300 bg-red-50 text-red-800';
  if (priority === 2) return 'border-amber-300 bg-amber-50 text-amber-800';
  return 'border-slate-200 bg-white text-slate-700';
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${tones[tone]}`}><Icon className="h-6 w-6" /></div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-600">{label}</div>
        </div>
      </div>
    </div>
  );
}

function CaseNumberWithCopy({ todo }) {
  const [copied, setCopied] = useState(false);
  if (!todo.case) return null;

  const caseNumber = todo.case.case_number || todo.case.case_id;
  const copyCaseNumber = async () => {
    try {
      await navigator.clipboard.writeText(caseNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      window.prompt('Copy this case number:', caseNumber);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <strong>Case:</strong> {caseNumber}
      <button
        type="button"
        onClick={copyCaseNumber}
        className="inline-flex items-center gap-1 font-semibold text-indigo-700 hover:text-indigo-900"
        aria-label={`Copy case number ${caseNumber}`}
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

function TodoCard({ todo, onComplete, onReopen, workingId }) {
  const [copiedLabel, setCopiedLabel] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const isCompleted = todo.status === 'completed';
  const isOpen = todo.status === 'open';
  const canMarkNotInterested = (
    ['consultation_followup', 'prospect_lawsuit_followup'].includes(todo.task_type)
    && todo.case
    && todo.case.status !== 'archived'
    && todo.status !== 'cancelled'
  );
  const configuredTexts = todo.metadata?.copyable_texts;
  const servicePacketKind = todo.metadata?.service_packet_kind;
  const servicePacket = (
    todo.task_type === 'service_documents'
    && ['lien_avoidance', 'garnishment', 'omitted_creditor'].includes(servicePacketKind)
    && todo.metadata?.packet_id
  ) ? {
      id: todo.metadata.packet_id,
      kind: servicePacketKind,
      filename: todo.metadata.packet_filename || (
        servicePacketKind === 'garnishment'
          ? 'garnishment-service-packet.zip'
          : servicePacketKind === 'omitted_creditor'
            ? 'omitted-creditor-service-packet.zip'
            : 'lien-avoidance-service-packet.zip'
      ),
      download_url: todo.metadata.packet_download_url || '',
      title: todo.metadata.packet_title || (
        servicePacketKind === 'garnishment'
          ? 'Garnishment service packet'
          : servicePacketKind === 'omitted_creditor'
            ? 'Omitted-creditor service packet'
            : 'Lien-avoidance service packet'
      ),
      description: todo.metadata.packet_description || (
        servicePacketKind === 'garnishment'
          ? 'Includes the notice, demand letters, supporting documents, service packets, and envelopes.'
          : servicePacketKind === 'omitted_creditor'
            ? 'Includes the omitted-creditor letter, selected bankruptcy documents, and the mailing envelope when requested.'
            : 'Includes the motion, proposed order, and envelopes.'
      ),
      recipient_count: todo.metadata.recipient_count || 0,
      creditor_name: todo.metadata.creditor_name || (
        servicePacketKind === 'garnishment'
          ? 'garnishing creditor'
          : servicePacketKind === 'omitted_creditor'
            ? 'omitted creditor'
            : 'judgment lien creditor'
      ),
    } : null;
  const copyableTexts = Array.isArray(configuredTexts) && configuredTexts.length > 0
    ? configuredTexts
    : todo.copy_text
      ? [{ label: todo.metadata?.copy_label || 'Copyable text', text: todo.copy_text }]
      : [];
  const trusteeDocumentUploads = useMemo(() => {
    const seen = new Set();
    return Object.values(todo.trustee_details?.document_downloads || {})
      .flat()
      .filter((upload) => {
        const key = String(upload?.id || '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [todo.trustee_details]);

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedLabel(label);
      window.setTimeout(() => setCopiedLabel(''), 1800);
    } catch (_) {
      window.prompt('Copy this text:', value);
    }
  };

  const downloadCertificate = async (certificate) => {
    setDownloadingId(certificate.id);
    setDownloadError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(certificate.download_url), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = certificate.filename || 'financial-management-certificate.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setDownloadError(error.message || 'Could not download the certificate.');
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadServicePacket = async (packet) => {
    const downloadKey = `service-packet-${packet.id}`;
    setDownloadingId(downloadKey);
    setDownloadError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(packet.download_url), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = packet.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setDownloadError(error.message || 'Could not download the service packet.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <article className={`rounded-xl border p-5 shadow-sm ${isCompleted ? 'border-green-200 bg-green-50/40' : priorityClasses(todo.priority)}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ring-slate-200">
              {todo.task_type_display}
            </span>
            {todo.priority <= 2 && !isCompleted && (
              <span className="rounded-full bg-red-600 px-2.5 py-1 text-xs font-semibold text-white">
                {todo.priority === 1 ? 'Urgent' : 'High priority'}
              </span>
            )}
            {isCompleted && <span className="text-xs font-semibold text-green-700">Completed {formatDateTime(todo.completed_at)}</span>}
          </div>
          <h3 className="mt-3 text-lg font-semibold text-slate-900">{todo.title}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-700">{todo.description}</p>

          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
            <span><strong>Assigned:</strong> {todo.employee?.full_name || 'Unassigned'}</span>
            <span><strong>Due:</strong> {formatDate(todo.due_at)}</span>
            {todo.task_type === 'trustee_documents' && (todo.case?.date_filed || todo.metadata?.date_filed) && (
              <span>
                <strong>Filed:</strong>{' '}
                {formatDate(todo.case?.date_filed || todo.metadata?.date_filed)}
              </span>
            )}
            <CaseNumberWithCopy todo={todo} />
            {todo.lead?.phone && (
              <span className="inline-flex items-center gap-1.5">
                <strong>Phone:</strong> {todo.lead.phone}
                <button
                  type="button"
                  onClick={() => copyText(todo.lead.phone, `lead-phone-${todo.lead.phone}`)}
                  className="inline-flex items-center gap-1 font-semibold text-indigo-700 hover:text-indigo-900"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copiedLabel === `lead-phone-${todo.lead.phone}` ? 'Copied' : 'Copy'}
                </button>
              </span>
            )}
          </div>

          {todo.recipient_phones?.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Text</span>
              {todo.recipient_phones.map((phone, index) => (
                <span key={`${phone}-${index}`} className="inline-flex overflow-hidden rounded-full bg-white ring-1 ring-indigo-200">
                  <a href={`sms:${phone}`} className="px-3 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50">
                    {phone}
                  </a>
                  <button
                    type="button"
                    onClick={() => copyText(phone, `recipient-phone-${phone}-${index}`)}
                    className="inline-flex items-center gap-1 border-l border-indigo-200 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                    aria-label={`Copy ${phone}`}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    {copiedLabel === `recipient-phone-${phone}-${index}` ? 'Copied' : 'Copy'}
                  </button>
                </span>
              ))}
            </div>
          )}

          {todo.trustee_details && (
            <div className="mt-4">
              <section className="rounded-lg border border-slate-200 bg-white p-4 text-slate-700">
                <h4 className="font-semibold text-slate-900">
                  Trustee documents (Chapter {todo.trustee_details.chapter})
                </h4>
                <div className="mt-2 space-y-1 text-sm">
                  <p><strong>Trustee:</strong> {todo.trustee_details.trustee || '—'}</p>
                  {todo.trustee_details.upload_site && (
                    <p>
                      <strong>Upload site:</strong>{' '}
                      <a
                        href={todo.trustee_details.upload_site}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-indigo-700 hover:underline"
                      >
                        Open trustee upload site
                      </a>
                    </p>
                  )}
                </div>
                {todo.trustee_details.documents.length > 0 ? (
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                    {todo.trustee_details.documents.map((documentName, index) => (
                      <li key={`${documentName}-${index}`} className={['Send Tax Refund Affidavit', 'DSO Form'].includes(documentName) ? 'list-none' : ''}>
                        {documentName === 'Send Tax Refund Affidavit' ? (
                          <TaxRefundAffidavitPanel
                            caseId={todo.case.case_id}
                            caseNumber={todo.case.case_number}
                            chapter={todo.trustee_details.chapter}
                            debtorEmails={todo.case.debtor_emails || []}
                            todoId={todo.id}
                            applicableOverride
                          />
                        ) : documentName === 'DSO Form' ? (
                          <DSOFormPanel
                            caseId={todo.case.case_id}
                            caseNumber={todo.case.case_number}
                            chapter={todo.trustee_details.chapter}
                            todoId={todo.id}
                          />
                        ) : (
                          <>
                            <span>{documentName}</span>
                            <TrusteeRequirementDocumentLinks
                              uploads={todo.trustee_details.document_downloads?.[documentName] || []}
                            />
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">No specific trustee documents derived yet.</p>
                )}
                <TrusteeDocumentMergePanel
                  caseId={todo.case.case_id}
                  caseNumber={todo.case.case_number}
                  uploads={trusteeDocumentUploads}
                />
              </section>
            </div>
          )}

          {servicePacket && (
            <div className="mt-4 rounded-lg border border-indigo-200 bg-white p-4 text-slate-700">
              <h4 className="font-semibold text-slate-900">{servicePacket.title}</h4>
              <p className="mt-1 text-sm leading-6">
                {servicePacket.description} Prepared for {servicePacket.recipient_count}{' '}
                recipient{servicePacket.recipient_count === 1 ? '' : 's'}, including{' '}
                {servicePacket.creditor_name}.
              </p>
              {servicePacket.download_url ? (
                <button
                  type="button"
                  onClick={() => downloadServicePacket(servicePacket)}
                  disabled={downloadingId === `service-packet-${servicePacket.id}`}
                  className="mt-3 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  {downloadingId === `service-packet-${servicePacket.id}`
                    ? 'Downloading…'
                    : 'Download saved packet'}
                </button>
              ) : (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  The application ZIP is unavailable. Open the packet page to use its OneDrive files.
                </p>
              )}
            </div>
          )}

          {todo.task_type === 'service_documents' && todo.case && !servicePacket && (
            <div className="mt-4">
              <ServiceDocumentsPanel
                caseId={todo.case.case_id}
                caseNumber={todo.case.case_number}
                chapter={todo.case.chapter}
                serviceDocs={todo.case.service_docs}
              />
            </div>
          )}

          {todo.task_type === 'bnc_compliance' && todo.case && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-white p-4 text-slate-700">
              <BNCCompliancePanel caseId={todo.case.case_id} compact />
            </div>
          )}

          {copyableTexts.map((item) => (
            <div key={item.label} className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-slate-700">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</span>
                <button type="button" onClick={() => copyText(item.text, item.label)} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:text-indigo-900">
                  <ClipboardDocumentIcon className="h-4 w-4" /> {copiedLabel === item.label ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 lg:flex-col">
          {todo.task_type === 'financial_management' && isOpen && (
            <FinancialManagementFilingPanel todo={todo} onFiled={onComplete} />
          )}
          {(todo.certificate_downloads || []).map((certificate, index) => (
            <button
              key={certificate.id}
              type="button"
              onClick={() => downloadCertificate(certificate)}
              disabled={downloadingId === certificate.id}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
              title={certificate.filename}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {downloadingId === certificate.id
                ? 'Downloading…'
                : `Download certificate${todo.certificate_downloads.length > 1 ? ` ${index + 1}` : ''}`}
            </button>
          ))}
          {downloadError && <p className="max-w-48 text-xs font-medium text-red-700">{downloadError}</p>}
          {todo.metadata?.public_record_url && (
            <a
              href={todo.metadata.public_record_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-indigo-300 bg-white px-4 py-2 text-center text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              Open public record
            </a>
          )}
          {todo.action_url && (
            <a href={todo.action_url} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {todo.task_type === 'meeting_341_work'
                ? 'Open 341 Workboard'
                : todo.lead
                  ? 'Open leads'
                  : 'Open case'}
            </a>
          )}
          {isCompleted && (
            <button
              type="button"
              onClick={() => onReopen(todo)}
              disabled={workingId === todo.id}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-60"
            >
              <ArrowUturnLeftIcon className="h-5 w-5" />
              {workingId === todo.id
                ? 'Reopening…'
                : todo.task_type === 'financial_management'
                  ? 'Mark certificate not filed'
                  : 'Mark not complete'}
            </button>
          )}
          {(canMarkNotInterested || isOpen) && (
            <>
              {canMarkNotInterested && (
                <button
                  type="button"
                  onClick={() => onComplete(todo, 'not_interested')}
                  disabled={workingId === todo.id}
                  className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  Mark not interested
                </button>
              )}
              {isOpen && (
                <button
                  type="button"
                  onClick={() => onComplete(todo)}
                  disabled={workingId === todo.id}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {workingId === todo.id ? 'Saving…' : 'Mark done'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function Celebration({ visible }) {
  if (!visible) return null;
  const bursts = [
    ['10%', '18%', '🎉', '0s'], ['23%', '68%', '✨', '0.12s'],
    ['39%', '22%', '🎆', '0.2s'], ['55%', '72%', '🎊', '0.08s'],
    ['72%', '20%', '✨', '0.28s'], ['86%', '64%', '🎆', '0.16s'],
    ['16%', '44%', '⭐', '0.34s'], ['81%', '42%', '🎉', '0.4s'],
  ];
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-live="polite">
      <style>{`
        @keyframes todo-burst {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(.25) rotate(-15deg); }
          25% { opacity: 1; transform: translate(-50%, -50%) scale(1.35) rotate(8deg); }
          100% { opacity: 0; transform: translate(-50%, -130%) scale(.8) rotate(24deg); }
        }
        @keyframes todo-huzzah {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(.5); }
          20%, 70% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -65%) scale(1.08); }
        }
      `}</style>
      {bursts.map(([left, top, symbol, delay]) => (
        <span
          key={`${left}-${top}`}
          className="absolute text-4xl"
          style={{ left, top, animation: `todo-burst 1.5s ease-out ${delay} both` }}
        >
          {symbol}
        </span>
      ))}
      <div
        className="absolute left-1/2 top-1/2 rounded-2xl bg-indigo-700 px-8 py-5 text-4xl font-black tracking-wide text-white shadow-2xl"
        style={{ animation: 'todo-huzzah 1.6s ease-out both' }}
      >
        Huzzah!
      </div>
    </div>
  );
}

function TodayView({ data, onComplete, onReopen, workingId, recentlyCompletedIds }) {
  const todos = data?.todos || [];
  const categoryTodos = todos.filter(
    (todo) => todo.status === 'open' || recentlyCompletedIds.has(todo.id),
  );
  const openTodos = categoryTodos.filter((todo) => todo.status === 'open');
  const completedTodos = todos.filter(
    (todo) => todo.status === 'completed' && !recentlyCompletedIds.has(todo.id),
  );
  const groups = categoryTodos.reduce((result, todo) => {
    const key = todo.task_type_display;
    if (!result[key]) result[key] = [];
    result[key].push(todo);
    return result;
  }, {});

  return (
    <div className="space-y-8">
      {data?.is_weekend && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-10 text-center">
          <ClockIcon className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-3 text-xl font-semibold text-indigo-900">No weekend todos</h2>
          <p className="mt-1 text-sm text-indigo-700">Today&apos;s ToDos resume on Monday.</p>
        </div>
      )}
      {openTodos.length === 0 && recentlyCompletedIds.size === 0 && !data?.is_weekend && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-10 text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
          <h2 className="mt-3 text-xl font-semibold text-green-900">Everything is complete</h2>
          <p className="mt-1 text-sm text-green-700">There are no open todos for today.</p>
        </div>
      )}

      {Object.entries(groups).map(([label, items]) => (
        <details key={label} className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <summary className="flex cursor-pointer items-center justify-between px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">{label}</h2>
            <span className="flex items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{items.length}</span>
              <ChevronDownIcon className="h-5 w-5 text-slate-500 transition-transform group-open:rotate-180" />
            </span>
          </summary>
          <div className="space-y-3 border-t border-slate-200 bg-slate-50 p-4">
            {items.map((todo) => <TodoCard key={todo.id} todo={todo} onComplete={onComplete} onReopen={onReopen} workingId={workingId} />)}
          </div>
        </details>
      ))}

      {completedTodos.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer font-semibold text-slate-900">Completed today ({completedTodos.length})</summary>
          <div className="mt-4 space-y-3">
            {completedTodos.map((todo) => <TodoCard key={todo.id} todo={todo} onComplete={onComplete} onReopen={onReopen} workingId={workingId} />)}
          </div>
        </details>
      )}
    </div>
  );
}

function HistoryTodoModal({ todo, onClose, onComplete, onReopen, workingId }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-todo-modal-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h2 id="history-todo-modal-title" className="text-lg font-semibold text-slate-900">
              Historical ToDo
            </h2>
            <p className="text-sm text-slate-500">
              Originally scheduled for {formatDate(todo.scheduled_for)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close todo details"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="bg-slate-50 p-4 sm:p-6">
          <TodoCard todo={todo} onComplete={onComplete} onReopen={onReopen} workingId={workingId} />
        </div>
      </div>
    </div>
  );
}

function HistoryView({ data, days, onComplete, onReopen, workingId }) {
  const [selectedTodoId, setSelectedTodoId] = useState(null);
  const performance = data?.performance || [];
  const todos = data?.todos || [];
  const selectedTodo = todos.find((todo) => todo.id === selectedTodoId) || null;
  const dailyGroups = Object.entries(todos.reduce((groups, todo) => {
    const day = todo.scheduled_for;
    if (!groups[day]) groups[day] = [];
    groups[day].push(todo);
    return groups;
  }, {})).sort(([dayA], [dayB]) => dayB.localeCompare(dayA));

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Employee performance — last {days} days</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {performance.map((row) => (
            <div key={row.employee.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="font-semibold text-slate-900">{row.employee.full_name}</div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-xl font-bold">{row.assigned}</div><div className="text-xs text-slate-500">Assigned</div></div>
                <div><div className="text-xl font-bold text-green-700">{row.completed}</div><div className="text-xs text-slate-500">Complete</div></div>
                <div><div className="text-xl font-bold text-indigo-700">{row.completion_rate}%</div><div className="text-xs text-slate-500">Rate</div></div>
              </div>
            </div>
          ))}
          {performance.length === 0 && <div className="text-sm text-slate-500">Add employees in Account Management to begin tracking performance.</div>}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Daily history</h2>
        <div className="space-y-3">
          {dailyGroups.map(([day, dayTodos]) => {
            const assigned = dayTodos.filter((todo) => todo.status !== 'cancelled').length;
            const completed = dayTodos.filter((todo) => todo.status === 'completed').length;
            const remaining = dayTodos.filter((todo) => todo.status === 'open').length;
            const cancelled = dayTodos.filter((todo) => todo.status === 'cancelled').length;
            const completionRate = assigned ? Math.round((completed / assigned) * 100) : 0;

            return (
              <details key={day} className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <summary className="cursor-pointer px-5 py-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-3">
                      <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-180" />
                      <div>
                        <h3 className="font-semibold text-slate-900">{formatDate(day)}</h3>
                        <p className="text-xs text-slate-500">{dayTodos.length} total record{dayTodos.length === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="rounded-lg bg-slate-100 px-3 py-2 text-center">
                        <div className="text-lg font-bold text-slate-900">{assigned}</div>
                        <div className="text-xs text-slate-500">Assigned</div>
                      </div>
                      <div className="rounded-lg bg-green-50 px-3 py-2 text-center">
                        <div className="text-lg font-bold text-green-700">{completed}</div>
                        <div className="text-xs text-green-700">Completed</div>
                      </div>
                      <div className="rounded-lg bg-amber-50 px-3 py-2 text-center">
                        <div className="text-lg font-bold text-amber-700">{remaining}</div>
                        <div className="text-xs text-amber-700">Remaining</div>
                      </div>
                      <div className="rounded-lg bg-indigo-50 px-3 py-2 text-center">
                        <div className="text-lg font-bold text-indigo-700">{completionRate}%</div>
                        <div className="text-xs text-indigo-700">Completion</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-green-500" style={{ width: `${completionRate}%` }} />
                  </div>
                </summary>

                <div className="overflow-x-auto border-t border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-4 py-3">Todo</th>
                        <th className="px-4 py-3">Employee</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Completed</th>
                        <th className="px-4 py-3 text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayTodos.map((todo) => (
                        <tr key={todo.id} className="border-t border-slate-200">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{todo.title}</div>
                            <div className="text-xs text-slate-500">{todo.task_type_display}</div>
                            {todo.case && (
                              <div className="mt-1 text-xs text-slate-600">
                                <CaseNumberWithCopy todo={todo} />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{todo.employee?.full_name || 'Unassigned'}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${todo.status === 'completed' ? 'bg-green-100 text-green-800' : todo.status === 'cancelled' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>
                              {todo.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTime(todo.completed_at)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => setSelectedTodoId(todo.id)}
                              className="whitespace-nowrap rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                            >
                              Open ToDo
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {cancelled > 0 && (
                    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                      {cancelled} cancelled todo{cancelled === 1 ? '' : 's'} excluded from the completion rate.
                    </div>
                  )}
                </div>
              </details>
            );
          })}

          {dailyGroups.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center text-slate-500 shadow-sm">
              No todo history for this period.
            </div>
          )}
        </div>
      </section>
      {selectedTodo && (
        <HistoryTodoModal
          todo={selectedTodo}
          onClose={() => setSelectedTodoId(null)}
          onComplete={onComplete}
          onReopen={onReopen}
          workingId={workingId}
        />
      )}
    </div>
  );
}

export default function TodaysTodosPage() {
  const [scope, setScope] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState(null);
  const [historyDays, setHistoryDays] = useState(30);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [recentlyCompletedIds, setRecentlyCompletedIds] = useState(() => new Set());
  const [celebrating, setCelebrating] = useState(false);

  const load = async (selectedScope = scope) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedScope === 'history') {
        params.set('scope', 'history');
        params.set('days', historyDays);
      }
      if (employeeFilter) params.set('employee', employeeFilter);
      const query = params.toString() ? `?${params.toString()}` : '';
      setData(await apiFetch(`/firm/todays-todos/${query}`));
      setRecentlyCompletedIds(new Set());
    } catch (err) {
      setError(err.message || 'Could not load todos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(scope); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope, historyDays, employeeFilter]);

  const complete = async (todo, outcome = 'completed') => {
    if (
      outcome === 'not_interested'
      && !window.confirm('Mark this person not interested and archive the prospect case?')
    ) return;

    setWorkingId(todo.id);
    setError('');
    try {
      const result = await apiFetch(`/firm/todays-todos/${todo.id}/complete/`, {
        method: 'POST',
        body: JSON.stringify({ outcome }),
      });
      if (result?.todo) {
        setData((current) => {
          if (!current) return current;
          const nextTodos = (current.todos || []).map((item) => (
            item.id === result.todo.id ? result.todo : item
          ));
          const open = nextTodos.filter((item) => item.status === 'open').length;
          const completed = nextTodos.filter((item) => item.status === 'completed').length;
          const overdue = nextTodos.filter((item) => (
            item.status === 'open'
            && item.due_at
            && isBeforeToday(item.due_at)
          )).length;
          return {
            ...current,
            todos: nextTodos,
            summary: { open, completed, overdue },
          };
        });
        if (todo.status === 'open') {
          setRecentlyCompletedIds((current) => {
            const next = new Set(current);
            next.add(result.todo.id);
            return next;
          });
        }
      }
      setCelebrating(false);
      window.requestAnimationFrame(() => setCelebrating(true));
      window.setTimeout(() => setCelebrating(false), 1800);
    } catch (err) {
      setError(err.message || 'Could not complete todo.');
    } finally {
      setWorkingId(null);
    }
  };

  const reopen = async (todo) => {
    const confirmation = todo.task_type === 'financial_management'
      ? 'Mark this certificate as not filed? This will reopen the ToDo and allow another CM/ECF filing attempt. The prior attempt and docket number will remain in the audit history.'
      : 'Mark this todo as not complete? Any case, packet, or follow-up status changed by completion will also be restored.';
    if (!window.confirm(confirmation)) return;

    setWorkingId(todo.id);
    setError('');
    setCelebrating(false);
    try {
      const result = await apiFetch(`/firm/todays-todos/${todo.id}/reopen/`, {
        method: 'POST',
      });
      if (result?.todo) {
        setData((current) => {
          if (!current) return current;
          const nextTodos = (current.todos || []).map((item) => (
            item.id === result.todo.id ? result.todo : item
          ));
          const open = nextTodos.filter((item) => item.status === 'open').length;
          const completed = nextTodos.filter((item) => item.status === 'completed').length;
          const overdue = nextTodos.filter((item) => (
            item.status === 'open'
            && item.due_at
            && isBeforeToday(item.due_at)
          )).length;
          const employeeId = result.todo.employee?.id;
          const performance = Array.isArray(current.performance)
            ? current.performance.map((row) => {
                if (!result.changed || !employeeId || row.employee?.id !== employeeId) return row;
                const rowCompleted = Math.max(0, row.completed - 1);
                return {
                  ...row,
                  completed: rowCompleted,
                  open: row.open + 1,
                  completion_rate: row.assigned
                    ? Math.round((rowCompleted / row.assigned) * 100)
                    : 0,
                };
              })
            : current.performance;
          return {
            ...current,
            todos: nextTodos,
            performance,
            summary: { open, completed, overdue },
          };
        });
        setRecentlyCompletedIds((current) => {
          const next = new Set(current);
          next.delete(result.todo.id);
          return next;
        });
      }
    } catch (err) {
      setError(err.message || 'Could not mark the todo as not complete.');
    } finally {
      setWorkingId(null);
    }
  };

  const summary = data?.summary || { open: 0, completed: 0, overdue: 0 };
  const dateLabel = useMemo(() => (
    data?.date ? new Date(`${data.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ''
  ), [data?.date]);

  return (
    <DashboardShell>
      <Celebration visible={celebrating} />
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Today&apos;s ToDos</h1>
            <p className="mt-1 text-slate-600">{dateLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={employeeFilter} onChange={(event) => setEmployeeFilter(event.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
              <option value="">All assignees</option>
              <option value="unassigned">Unassigned</option>
              {(data?.employees || []).map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.full_name}</option>
              ))}
            </select>
            <button onClick={() => setScope('today')} className={`rounded-md px-4 py-2 text-sm font-semibold ${scope === 'today' ? 'bg-indigo-600 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>Today</button>
            <button onClick={() => setScope('history')} className={`rounded-md px-4 py-2 text-sm font-semibold ${scope === 'history' ? 'bg-indigo-600 text-white' : 'border border-slate-300 bg-white text-slate-700'}`}>History</button>
            {scope === 'history' && (
              <select value={historyDays} onChange={(event) => setHistoryDays(Number(event.target.value))} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
              </select>
            )}
            <button onClick={() => load(scope)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Refresh</button>
          </div>
        </div>

        {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800">{error}</div>}

        {scope === 'today' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SummaryCard label="Open" value={summary.open} icon={ClockIcon} tone="indigo" />
            <SummaryCard label="Completed today" value={summary.completed} icon={CheckCircleIcon} tone="green" />
            <SummaryCard label="Past deadline" value={summary.overdue} icon={ExclamationTriangleIcon} tone="red" />
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-600">Building today&apos;s list…</div>
        ) : scope === 'today' ? (
          <TodayView
            data={data}
            onComplete={complete}
            onReopen={reopen}
            workingId={workingId}
            recentlyCompletedIds={recentlyCompletedIds}
          />
        ) : (
          <HistoryView
            data={data}
            days={historyDays}
            onComplete={complete}
            onReopen={reopen}
            workingId={workingId}
          />
        )}
      </div>
    </DashboardShell>
  );
}
